/**
 * Persists the Storyfinder result in localStorage.
 *
 * The visitor should be able to leave the result page, browse to one of the
 * recommendations and come back without losing the advice. The wizard state
 * itself is deliberately not stored: only the result that was shown.
 */

const STORAGE_KEY = 'vneml_storyfinder_result';

/**
 * Bumped whenever the stored shape changes, so old entries are discarded
 * instead of feeding an outdated structure into the card component.
 */
const STORAGE_VERSION = 2;

/**
 * How long a stored result stays valid.
 *
 * Cards hold image derivative URLs and node paths, which go stale once content
 * is edited or unpublished. A day is long enough to survive a browsing session
 * and short enough to avoid resurrecting dead links weeks later.
 */
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Reads the stored result for a persona.
 *
 * @param {string} personaKey
 *   The persona the visitor is currently using.
 *
 * @return {{adviceText: string, storyText: string, groups: Array}|null}
 *   The stored result, or null when there is nothing usable to restore.
 */
export function loadResult(personaKey) {
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    // Storage can be unavailable, for instance in private browsing modes.
    return null;
  }

  if (!raw) {
    return null;
  }

  let stored;
  try {
    stored = JSON.parse(raw);
  } catch (e) {
    clearResult();
    return null;
  }

  if (!stored || (stored.version !== STORAGE_VERSION && stored.version !== 1)) {
    clearResult();
    return null;
  }

  // A result belongs to the persona that produced it. Picking another
  // verhaalvinder has to start over rather than show someone else's advice.
  if ((stored.personaKey || '') !== (personaKey || '')) {
    clearResult();
    return null;
  }

  if (!stored.savedAt || Date.now() - stored.savedAt > MAX_AGE_MS) {
    clearResult();
    return null;
  }

  if (!Array.isArray(stored.groups)) {
    clearResult();
    return null;
  }

  return {
    adviceText: typeof stored.adviceText === 'string' ? stored.adviceText : '',
    storyText: typeof stored.storyText === 'string' ? stored.storyText : '',
    groups: stored.groups,
  };
}

/**
 * Stores the result that is currently shown.
 *
 * @param {string} personaKey
 *   The persona that produced this result.
 * @param {string} adviceText
 *   The advice intro text.
 * @param {Array} groups
 *   The card groups.
 * @param {string} storyText
 *   The shared story text in we-form.
 */
export function saveResult(personaKey, adviceText, groups, storyText = '') {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: STORAGE_VERSION,
      savedAt: Date.now(),
      personaKey: personaKey || '',
      adviceText: adviceText || '',
      storyText: storyText || '',
      groups: Array.isArray(groups) ? groups : [],
    }));
  } catch (e) {
    // A failing write (storage full or unavailable) must not break the wizard.
  }
}

/**
 * Removes the stored result.
 */
export function clearResult() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Nothing to do: there is no result left to show either way.
  }
}
